import React, { useCallback, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import { GridApi } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Wrapper } from './AgGrid.styles';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';

interface IAgGridProps extends React.ComponentProps<typeof AgGridReact> {
  className?: string;
  height?: string;
  width?: string;
}

export const AgGrid = ({
  className,
  height,
  width,
  onGridReady: onGridReadyProp,
  onGridColumnsChanged: onGridColumnsChangedProp,
  ...props
}: IAgGridProps) => {
  const gridApi = useRef<GridApi>();

  const sizeColumnsToFit = useCallback(
    debounce(() => {
      if (gridApi && gridApi.current) {
        // @ts-ignore
        gridApi.current.sizeColumnsToFit();
      }
    }, 50),
    []
  );
  useEffect(() => {
    window.addEventListener('resize', sizeColumnsToFit);
    return () => {
      sizeColumnsToFit.cancel();
      window.removeEventListener('resize', sizeColumnsToFit);
    };
  }, [sizeColumnsToFit]);

  const onGridReady = useCallback(
    (params) => {
      gridApi.current = params.api;
      sizeColumnsToFit();

      if (onGridReadyProp) {
        onGridReadyProp(params);
      }
    },
    [sizeColumnsToFit, onGridReadyProp]
  );
  const onGridColumnsChanged = useCallback(
    (args) => {
      if (onGridColumnsChangedProp) {
        onGridColumnsChangedProp(args);
      }
      sizeColumnsToFit();
    },
    [sizeColumnsToFit, onGridColumnsChangedProp]
  );
  return (
    <Wrapper>
      <div className={`${className} ag-table`} style={{ width, height }}>
        <AgGridReact
          rowHeight={49}
          headerHeight={49}
          suppressCopyRowsToClipboard
          suppressScrollOnNewData
          suppressRowHoverHighlight
          suppressRowDrag
          suppressRowClickSelection
          suppressCellSelection
          suppressCsvExport
          suppressExcelExport
          suppressContextMenu
          onGridReady={onGridReady}
          onGridColumnsChanged={onGridColumnsChanged}
          {...props}
        />
      </div>
    </Wrapper>
  );
};

AgGrid.propTypes = {
  className: PropTypes.string,
  columnDefs: PropTypes.arrayOf(
    PropTypes.shape({
      headerName: PropTypes.string,
      suppressMenu: PropTypes.bool,
      field: PropTypes.string,
      width: PropTypes.number,
    })
  ),
};

AgGrid.defaultProps = {
  className: 'ag-theme-balham',
  columnDefs: [],
  rowData: [],
  width: '100%',
  height: '100%',
};

export default AgGrid;
